import React, { useState, useEffect, useCallback } from "react";
import { NavLink as RouterLink, Link, useLocation, useHistory } from "react-router-dom";
import makeStyles from "@material-ui/styles/makeStyles";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Zoom from "@material-ui/core/Zoom";
import Alert from "@material-ui/lab/Alert";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import BookmarkBorderOutlinedIcon from "@material-ui/icons/BookmarkBorderOutlined";
import Drawer from "@material-ui/core/Drawer";
import {
  ProSidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  Menu,
  MenuItem,
  SubMenu,
} from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";

import { useGlobalState } from "../../../../state";
import { pages } from "../Sidebar/Sidebar";
import { version } from "../../../../common/enums";
import api from "../../../../common/api";

export const SIDEBAR_WIDTH_EXPANDED = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 80;
const MOBILE_BAR_HEIGHT = 56;
const BG = "#2c3e50";
const ACTIVE = "#f3b56b";

const getInitials = (title) => {
  if (!title) return "??";
  const words = title.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return title.substring(0, 2).toUpperCase();
};

/* ── Styles only for EXPANDED mode (ProSidebar overrides) ── */
const useStyles = makeStyles((theme) => ({
  expandedRoot: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: SIDEBAR_WIDTH_EXPANDED,
    zIndex: theme.zIndex.drawer,
    "& .pro-sidebar": {
      width: "100%",
      minWidth: "100%",
      height: "100%",
      color: "#fff",
    },
    "& .pro-sidebar-inner": {
      background:  BG,
    },
    "& .pro-sidebar .pro-menu .pro-menu-item": { fontSize: 14 },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item": {
      padding: "8px 20px",
      transition: "color 0.2s",
    },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:hover": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },
    "& .pro-sidebar .pro-menu .pro-menu-item.active > .pro-inner-item": {
      backgroundColor: "rgba(243,181,107,0.15)",
      borderRadius: 8,
    },
    "& .pro-sidebar .pro-menu a": { color: "inherit", textDecoration: "none" },
    "& .pro-sidebar .pro-menu a:hover": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },
    "& .pro-sidebar .pro-menu a.active": {
      color: theme.palette.navActive || ACTIVE,
    },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item > .pro-icon-wrapper":
      {
        minWidth: 24,
        width: 24,
        height: 24,
        lineHeight: "24px",
        fontSize: 20,
        marginRight: 10,
        color: theme.palette.icon || "#c5c7c9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "color 0.2s",
      },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item > .pro-icon-wrapper .pro-icon":
      {
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        animation: "none !important",
      },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:hover .pro-icon-wrapper .pro-icon":
      {
        animation: "none !important",
      },
    "& .pro-sidebar .pro-menu .pro-menu-item.active > .pro-inner-item > .pro-icon-wrapper":
      {
        color: theme.palette.navActive || ACTIVE,
      },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:hover > .pro-icon-wrapper":
      {
        color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
      },
    "& .pro-sidebar .pro-menu .pro-menu-item .suffix-wrapper": {
      opacity: 0.7,
    },
    "& .pro-sidebar .pro-menu > ul > .pro-sub-menu > .pro-inner-list-item": {
      backgroundColor: "transparent",
      paddingLeft: 16,
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-inner-item":
      {
        padding: "10px 16px 10px 20px",
      },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-inner-item:before":
      {
        display: "none",
      },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item":
      {
        borderLeft: "2px solid rgba(255,255,255,0.08)",
        marginLeft: 12,
        marginBottom: 2,
      },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item:hover":
      {
        borderLeftColor: "rgba(243,181,107,0.4)",
      },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item.active":
      {
        borderLeftColor: theme.palette.navActive || ACTIVE,
      },
    "& .pro-sidebar .pro-menu .pro-sub-menu > .pro-inner-item > .pro-arrow-wrapper .pro-arrow":
      {
        borderColor:
          theme.palette.navNestedMenuArrow || "rgba(255,255,255,0.7)",
      },
    "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu .pro-inner-list-item .pro-inner-item > .pro-icon-wrapper":
      {
        display: "none",
      },
  },
}));

const SidebarPro = ({ onExpandChange }) => {
  const [{ user, sidebarCollapsed }, dispatch] = useGlobalState();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"), {
    defaultMatches: true,
  });
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();

  var collapsed = sidebarCollapsed || false;
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [flyout, setFlyout] = useState({ menu: null, top: 0 });
  const [allPages, setAllPages] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const navPages = pages({ user, isDesktop });

  // ── Bookmarks ──
  const fetchBookmarks = useCallback(() => {
    const base = navPages.filter((p) => p.id === undefined);
    var total = [...base];
    api
      .get("get-all-bookmarks")
      .then((res) => {
        setBookmarks(res || []);
        var idx =
          user.versionID && user.versionID !== version.proPlus ? 1 : 2;
        total.splice(idx, 0, ...res);
      })
      .catch((err) => {
        if (err.message) {
          if (
            err.message === "Bad Request" ||
            err.message === "No data found"
          ) {
            setErrorMessage("Bookmarks not found");
          }
        } else {
          setErrorMessage("Unknown Error");
        }
      })
      .finally(() => setAllPages(total));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isDesktop]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  var removeBookmark = (page) => {
    var bookmark = bookmarks.find(
      (b) => b.title === page.title && b.href === page.href
    );
    api
      .post("remove-bookmark", bookmark || page)
      .then(() => {
        // Update bookmarks + allPages locally so SubMenu stays open
        setBookmarks((prev) =>
          prev.filter((b) => !(b.title === page.title && b.href === page.href))
        );
        setAllPages((prev) =>
          prev.filter(
            (p) =>
              !(
                p.isBookmarked === true &&
                p.title === page.title &&
                p.href === page.href
              )
          )
        );
      })
      .catch((err) => {
        if (err.message) {
          if (
            err.message === "Bad Request" ||
            err.message === "No data found"
          ) {
            setErrorMessage("Remove bookmark failed");
          } else {
            setErrorMessage(err.message);
          }
        } else {
          setErrorMessage("Unknown Error");
        }
      });
  };

  var createBookmark = (page) => {
    api
      .post("create-new-bookmark", { title: page.title, href: page.href })
      .then((res) => {
        // Update bookmarks locally so icon toggles without re-rendering menu
        var newBookmark = res || {
          title: page.title,
          href: page.href,
          isBookmarked: true,
        };
        setBookmarks((prev) => [...prev, newBookmark]);
      })
      .catch((err) => {
        if (err.message) {
          if (
            err.message === "Bad Request" ||
            err.message === "No data found"
          ) {
            setErrorMessage("Bookmark not added");
          } else {
            setErrorMessage(err.message);
          }
        } else {
          setErrorMessage("Unknown Error");
        }
      });
  };

  var isPageBookmarked = (page) => {
    return bookmarks.some(
      (b) => b.title === page.title && b.href === page.href
    );
  };

  var handleToggle = () => {
    var next = !collapsed;
    dispatch({ type: "setSidebarCollapsed", payload: next });
    if (onExpandChange) onExpandChange(!next);
  };

  var handleLogout = () => dispatch({ type: "logout" });

  var displayPages = allPages.length > 0 ? allPages : navPages;

  // Check if a page or any of its children match the current route
  var isActive = (page) => {
    if (page.href) return location.pathname.startsWith(page.href);
    if (page.subMenus) {
      return page.subMenus.some((s) => {
        if (s.href && location.pathname.startsWith(s.href)) return true;
        if (s.subMenus)
          return s.subMenus.some(
            (d) => d.href && location.pathname.startsWith(d.href)
          );
        return false;
      });
    }
    return false;
  };

  // Get first navigable href from a page
  var getHref = (page) => {
    if (page.href) return page.href;
    if (page.subMenus && page.subMenus.length > 0) {
      var first = page.subMenus[0];
      if (first.href) return first.href;
      if (first.subMenus && first.subMenus.length > 0)
        return first.subMenus[0].href;
    }
    return "/";
  };

  // Pages that have something to show (icon, nested, or bookmark)
  var visiblePages = displayPages.filter(
    (p) => p.icon || p.nested || p.isBookmarked === true
  );

  // Theme colors
  var navBg = BG;
  var activeColor = theme.palette.navActive || ACTIVE;
  var iconColor = theme.palette.icon || "#c5c7c9";

  // Profile
  var profileName = user.firstName + " " + user.lastName;
  var profileAvatar = user.profilePic || "/images/chick.webp";
  var profileRole = user.role || "Managing Director";

  // Reusable style objects
  var letterIcon = {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.12)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    lineHeight: 1,
  };

  var smallLetterIcon = {
    width: 22,
    height: 22,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.12)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    lineHeight: 1,
  };

  // ════════════════════════════════════════════
  // MOBILE
  // ════════════════════════════════════════════
  if (!isDesktop) {
    var tabs = visiblePages.slice(0, 4);
    var overflow = visiblePages.slice(4);
    var isMoreActive = overflow.some(isActive);

    return (
      <>
        {/* Bottom Nav Bar */}
        <nav
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: MOBILE_BAR_HEIGHT,
            backgroundColor: navBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            zIndex: theme.zIndex.appBar + 1,
            boxShadow: "0 -2px 12px rgba(0,0,0,0.2)",
            padding: "0 4px",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {tabs.map((page) => {
            var active = isActive(page);
            return (
              <button
                key={page.title}
                onClick={() => history.push(getHref(page))}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  height: "100%",
                  padding: "6px 0 4px",
                  border: "none",
                  background: "transparent",
                  color: active ? activeColor : "rgba(255,255,255,0.5)",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 22, marginBottom: 2 }}>
                  {page.icon || (
                    <span style={smallLetterIcon}>
                      {getInitials(page.title)}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {page.title}
                </span>
                {active && (
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: activeColor,
                      marginTop: 2,
                    }}
                  />
                )}
              </button>
            );
          })}

          {overflow.length > 0 && (
            <button
              onClick={() => setMobileDrawerOpen(true)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                height: "100%",
                padding: "6px 0 4px",
                border: "none",
                background: "transparent",
                color: isMoreActive
                  ? activeColor
                  : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                minWidth: 0,
              }}
            >
              <span style={{ fontSize: 22, marginBottom: 2 }}>
                <MoreHorizIcon />
              </span>
              <span style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.2 }}>
                More
              </span>
              {isMoreActive && (
                <span
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: activeColor,
                    marginTop: 2,
                  }}
                />
              )}
            </button>
          )}
        </nav>

        {/* More Drawer */}
        <Drawer
          anchor="bottom"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{
            style: {
              backgroundColor: navBg,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "80%",
            },
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.3)",
              margin: "10px auto 4px",
            }}
          />
          <div style={{ padding: "8px 16px 16px", overflowY: "auto" }}>
            {overflow.map((page) => {
              if (page.nested && page.subMenus) {
                return (
                  <React.Fragment key={page.title}>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        padding: "8px 8px 4px",
                      }}
                    >
                      {page.title}
                    </div>
                    {page.subMenus.map((sub) => {
                      if (sub.nested && sub.subMenus) {
                        return sub.subMenus.map((deep) => {
                          var deepActive =
                            deep.href &&
                            location.pathname.startsWith(deep.href);
                          return (
                            <RouterLink
                              key={deep.title}
                              to={deep.href || "/"}
                              onClick={() => setMobileDrawerOpen(false)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 12px 10px 28px",
                                borderRadius: 10,
                                textDecoration: "none",
                                color: deepActive ? activeColor : "#fff",
                                backgroundColor: deepActive
                                  ? "rgba(243,181,107,0.1)"
                                  : "transparent",
                                fontSize: 14,
                              }}
                            >
                              {deep.title}
                            </RouterLink>
                          );
                        });
                      }
                      var subActive =
                        sub.href && location.pathname.startsWith(sub.href);
                      return (
                        <RouterLink
                          key={sub.title}
                          to={sub.href || "/"}
                          onClick={() => setMobileDrawerOpen(false)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 12px 10px 28px",
                            borderRadius: 10,
                            textDecoration: "none",
                            color: subActive ? activeColor : "#fff",
                            backgroundColor: subActive
                              ? "rgba(243,181,107,0.1)"
                              : "transparent",
                            fontSize: 14,
                          }}
                        >
                          {sub.title}
                        </RouterLink>
                      );
                    })}
                  </React.Fragment>
                );
              }

              var active = isActive(page);
              return (
                <RouterLink
                  key={page.title + (page.id || "")}
                  to={page.href || "/"}
                  onClick={() => setMobileDrawerOpen(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    textDecoration: "none",
                    color: active ? activeColor : "#fff",
                    backgroundColor: active
                      ? "rgba(243,181,107,0.1)"
                      : "transparent",
                    fontSize: 14,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      color: active ? activeColor : "rgba(255,255,255,0.5)",
                    }}
                  >
                    {page.icon || (
                      <span style={smallLetterIcon}>
                        {getInitials(page.title)}
                      </span>
                    )}
                  </span>
                  {page.title}
                </RouterLink>
              );
            })}

            <div
              style={{
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                margin: "8px 0",
              }}
            />
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "none",
                background: "transparent",
                color: "#ff6b6b",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <ExitToAppIcon style={{ fontSize: 20 }} />
              Logout
            </button>
          </div>
        </Drawer>
      </>
    );
  }

  // ════════════════════════════════════════════
  // DESKTOP — COLLAPSED (custom div, no ProSidebar)
  // ════════════════════════════════════════════
  if (collapsed) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: SIDEBAR_WIDTH_COLLAPSED,
          height: "100vh",
          backgroundColor: navBg,
          display: "flex",
          flexDirection: "column",
          zIndex: theme.zIndex.drawer,
        }}
      >
        {/* Logo + Toggle */}
        <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
          <Link to="/">
            <img
              alt="Logo"
              src="/images/logos/poultry-client-logo.png"
              style={{ width: 36 }}
            />
          </Link>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "4px 0",
            }}
          >
            <IconButton
              onClick={handleToggle}
              size="small"
              style={{
                width: 28,
                height: 28,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: iconColor,
              }}
            >
              <ChevronRightIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
        </div>

        {/* Menu Items */}
        <div
          style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}
          onScroll={() => setFlyout({ menu: null, top: 0 })}
        >
          {displayPages.map((page) => {
            // Skip no-icon, non-bookmark, non-nested
            if (!page.icon && !page.nested && page.isBookmarked !== true)
              return null;

            var active = isActive(page);

            // ── SubMenu item: icon + flyout on hover ──
            if (page.nested && page.subMenus) {
              return (
                <div
                  key={page.title}
                  onMouseEnter={(e) => {
                    var rect = e.currentTarget.getBoundingClientRect();
                    setFlyout({ menu: page.title, top: rect.top });
                  }}
                  onMouseLeave={() => setFlyout({ menu: null, top: 0 })}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px 0",
                      margin: "2px 8px",
                      borderRadius: 8,
                      cursor: "pointer",
                      backgroundColor: active
                        ? "rgba(243,181,107,0.15)"
                        : "transparent",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        color: active ? activeColor : iconColor,
                        fontSize: 20,
                      }}
                    >
                      {page.icon || (
                        <span style={letterIcon}>
                          {getInitials(page.title)}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Flyout panel */}
                  {flyout.menu === page.title && (
                    <div
                      ref={(el) => {
                        if (el) {
                          var rect = el.getBoundingClientRect();
                          if (rect.bottom > window.innerHeight) {
                            el.style.top = (window.innerHeight - rect.height - 8) + "px";
                          }
                        }
                      }}
                      style={{
                        position: "fixed",
                        left: SIDEBAR_WIDTH_COLLAPSED,
                        top: flyout.top,
                        backgroundColor: navBg,
                        borderRadius: 8,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                        padding: "8px 0",
                        minWidth: 200,
                        maxHeight: "calc(100vh - 16px)",
                        overflowY: "auto",
                        zIndex: 9999,
                      }}
                    >
                      <div
                        style={{
                          padding: "6px 16px 8px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "rgba(255,255,255,0.5)",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {page.title}
                      </div>
                      {page.subMenus.map((sub) => {
                        if (sub.nested && sub.subMenus) {
                          return (
                            <React.Fragment key={sub.title}>
                              <div
                                style={{
                                  padding: "6px 16px 4px",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "rgba(255,255,255,0.4)",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  marginTop: 4,
                                }}
                              >
                                {sub.title}
                              </div>
                              {sub.subMenus.map((deep) => {
                                var dActive =
                                  deep.href &&
                                  location.pathname.startsWith(deep.href);
                                return (
                                  <RouterLink
                                    key={deep.title}
                                    to={deep.href || "/"}
                                    style={{
                                      display: "block",
                                      padding: "8px 16px 8px 24px",
                                      color: dActive ? activeColor : "#fff",
                                      textDecoration: "none",
                                      fontSize: 14,
                                      backgroundColor: dActive
                                        ? "rgba(243,181,107,0.1)"
                                        : "transparent",
                                    }}
                                  >
                                    {deep.title}
                                  </RouterLink>
                                );
                              })}
                            </React.Fragment>
                          );
                        }
                        var sActive =
                          sub.href && location.pathname.startsWith(sub.href);
                        return (
                          <RouterLink
                            key={sub.title}
                            to={sub.href || "/"}
                            style={{
                              display: "block",
                              padding: "8px 16px",
                              color: sActive ? activeColor : "#fff",
                              textDecoration: "none",
                              fontSize: 14,
                              backgroundColor: sActive
                                ? "rgba(243,181,107,0.1)"
                                : "transparent",
                            }}
                          >
                            {sub.title}
                          </RouterLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // ── Regular item: icon link + tooltip ──
            return (
              <Tooltip
                key={page.title + (page.id || "")}
                title={page.title}
                placement="right"
                arrow
              >
                <RouterLink
                  to={page.href || "/"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "10px 0",
                    margin: "2px 8px",
                    borderRadius: 8,
                    textDecoration: "none",
                    backgroundColor: active
                      ? "rgba(243,181,107,0.15)"
                      : "transparent",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      color: active ? activeColor : iconColor,
                      fontSize: 20,
                    }}
                  >
                    {page.icon || (
                      <span style={letterIcon}>
                        {getInitials(page.title)}
                      </span>
                    )}
                  </span>
                </RouterLink>
              </Tooltip>
            );
          })}
        </div>

        {/* Logout */}
        <div style={{ padding: "8px 0 16px", textAlign: "center" }}>
          <Tooltip title="Logout" placement="right" arrow>
            <IconButton
              onClick={handleLogout}
              style={{
                color:
                  theme.palette.logoutSidebar
                    ? theme.palette.logoutSidebar.background
                    : ACTIVE,
              }}
            >
              <ExitToAppIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════
  // DESKTOP — EXPANDED (ProSidebar)
  // ════════════════════════════════════════════
  return (
    <div className={classes.expandedRoot}>
      <ProSidebar>
        <SidebarHeader>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "16px 8px",
              position: "relative",
            }}
          >
            <Link to="/">
              <img
                alt="Logo"
                src="/images/logos/poultry-client-logo.png"
                style={{ width: 50, marginBottom: 8 }}
              />
            </Link>

            {/* Profile */}
            <div
              style={{
                textAlign: "center",
                backgroundColor:
                  theme.palette.profile
                    ? theme.palette.profile.box
                    : "rgba(255,255,255,0.3)",
                padding: 12,
                borderRadius: 12,
                width: "100%",
                marginTop: 8,
              }}
            >
              <Avatar
                alt="Person"
                src={profileAvatar}
                style={{
                  width: 50,
                  height: 50,
                  margin: "0 auto",
                  marginBottom: 4,
                }}
              />
              <Typography
                variant="body1"
                style={{
                  color:
                    theme.palette.profile
                      ? theme.palette.profile.name
                      : "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {profileName}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  color:
                    theme.palette.profile
                      ? theme.palette.profile.bio
                      : ACTIVE,
                  fontSize: 12,
                }}
              >
                {profileRole} ({user.versionName})
              </Typography>
            </div>

            {/* Toggle */}
            <div style={{ position: "absolute", top: 8, right: 8 }}>
              <IconButton
                onClick={handleToggle}
                size="small"
                style={{
                  width: 28,
                  height: 28,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  color: iconColor,
                }}
              >
                <ChevronLeftIcon style={{ fontSize: 18 }} />
              </IconButton>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          {errorMessage && (
            <div style={{ margin: "0 8px 8px" }}>
              <Alert severity="error" onClose={() => setErrorMessage("")}>
                {errorMessage}
              </Alert>
            </div>
          )}

          <Menu>
            {displayPages.map((page) => {
              // Nested → SubMenu
              if (page.nested && page.subMenus) {
                return (
                  <SubMenu
                    key={page.title}
                    title={page.title}
                    icon={
                      page.icon || (
                        <span style={letterIcon}>
                          {getInitials(page.title)}
                        </span>
                      )
                    }
                  >
                    {page.subMenus.map((sub) => {
                      if (sub.nested && sub.subMenus) {
                        return (
                          <SubMenu key={sub.title} title={sub.title}>
                            {sub.subMenus.map((deep) => (
                              <MenuItem
                                key={deep.title}
                                suffix={
                                  deep.href ? (
                                    <Tooltip
                                      title={isPageBookmarked(deep) ? "Remove bookmark" : "Add bookmark"}
                                      placement="top"
                                      arrow
                                      TransitionComponent={Zoom}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (isPageBookmarked(deep)) {
                                            removeBookmark(deep);
                                          } else {
                                            createBookmark(deep);
                                          }
                                        }}
                                        style={{ padding: 2, color: iconColor }}
                                      >
                                        {isPageBookmarked(deep) ? (
                                          <BookmarkIcon style={{ fontSize: 16 }} />
                                        ) : (
                                          <BookmarkBorderOutlinedIcon style={{ fontSize: 16 }} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  ) : undefined
                                }
                              >
                                <RouterLink
                                  to={deep.href}
                                  activeClassName="active"
                                >
                                  {deep.title}
                                </RouterLink>
                              </MenuItem>
                            ))}
                          </SubMenu>
                        );
                      }
                      return (
                        <MenuItem
                          key={sub.title}
                          suffix={
                            sub.href ? (
                              <Tooltip
                                title={isPageBookmarked(sub) ? "Remove bookmark" : "Add bookmark"}
                                placement="top"
                                arrow
                                TransitionComponent={Zoom}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isPageBookmarked(sub)) {
                                      removeBookmark(sub);
                                    } else {
                                      createBookmark(sub);
                                    }
                                  }}
                                  style={{ padding: 2, color: iconColor }}
                                >
                                  {isPageBookmarked(sub) ? (
                                    <BookmarkIcon style={{ fontSize: 16 }} />
                                  ) : (
                                    <BookmarkBorderOutlinedIcon style={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            ) : undefined
                          }
                        >
                          <RouterLink to={sub.href} activeClassName="active">
                            {sub.title}
                          </RouterLink>
                        </MenuItem>
                      );
                    })}
                  </SubMenu>
                );
              }

              // Has icon → regular item (no bookmark — already top-level)
              if (page.icon) {
                return (
                  <MenuItem key={page.title} icon={page.icon}>
                    <RouterLink to={page.href} activeClassName="active">
                      {page.title}
                    </RouterLink>
                  </MenuItem>
                );
              }

              // Bookmark item
              if (page.isBookmarked === true) {
                return (
                  <MenuItem
                    key={page.id + "-" + page.title}
                    icon={
                      <span style={letterIcon}>
                        {getInitials(page.title)}
                      </span>
                    }
                    suffix={
                      <Tooltip
                        title="Remove bookmark"
                        placement="top"
                        arrow
                        TransitionComponent={Zoom}
                      >
                        <BookmarkIcon
                          style={{
                            color: iconColor,
                            fontSize: 18,
                            cursor: "pointer",
                            marginLeft: 4,
                          }}
                          fontSize="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeBookmark(page);
                          }}
                        />
                      </Tooltip>
                    }
                  >
                    <RouterLink to={page.href} activeClassName="active">
                      {page.title}
                    </RouterLink>
                  </MenuItem>
                );
              }

              // No icon, not bookmarked → skip
              return null;
            })}
          </Menu>
        </SidebarContent>

        <SidebarFooter>
          <div
            style={{
              padding: "8px 16px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <ExitToAppIcon
                style={{
                  color:
                    theme.palette.logoutSidebar
                      ? theme.palette.logoutSidebar.background
                      : ACTIVE,
                  fontSize: 22,
                }}
              />
              Logout
            </button>
          </div>
        </SidebarFooter>
      </ProSidebar>
    </div>
  );
};

export default SidebarPro;
